<script>
import { fandomBasicMixin, fandomExtendedMixin } from 'src/modules/fandom_mixins_module.js'
import { sendFandomAnalyticsEvent }  from "src/modules/fandom_analytics_module.js";
import { interactionMixin } from 'src/modules/interactions_module.js'
import { get } from "lodash";

export default {
  mixins: [fandomBasicMixin, fandomExtendedMixin, interactionMixin],
  methods: {
    pinClick(pinRecord) {
      if (pinRecord.expanded) {
        Vue.delete(pinRecord, 'expanded');
      } else {
        this.content.pins.forEach(pin => {
          Vue.delete(pin, 'expanded');
        });
        this.updatePinInteraction(this.containerContent, pinRecord, pinRecord.interaction, null, () => {
          Vue.set(pinRecord, 'expanded', true);
        });
      }
    },
    linkClick(pin) {
      if (pin.tracking_params)
        sendFandomAnalyticsEvent(...(pin.tracking_params.split(",")));
      window.open(pin.url.url, this.getTarget(pin.url));
    },
    getPinnableAreaLayout(pin) {
      return {
        "top": pin.coord_y,
        "left": pin.coord_x,
        "background-color": pin.color
      }
    },
  },
  computed: {
    containerClasses() {
      const classes = [];
      if (this.propagateBackground) {
        classes.push("themed-item");
        classes.push(this.paddingY);
      }
      return classes;
    },
    contentClasses() {
      const classes = [];
      if (!this.propagateBackground) {
        classes.push("themed-item");
        classes.push(this.paddingY);
      }
      return classes;
    },
    propagateBackground() {
      return get(this, ["content", "propagate_container"], get(this, ["content", "propagate_background"], false));
    }
  }
};
</script>

<template>
  <div v-easyadmin="easyadminId" :class="[contentType, containerClasses]" v-if="isContentVisibleToUser()" :style="theme" interaction-multiple-pin-component>
    <div :class="content.propagate_container ? 'container-fluid' : 'container'">
      <div class="row justify-content-center bg-cover-center" :class="contentClasses">
        <div class="col-12" :class="`col-md-${content.width || 12}`">
          <div class="row">
            <img class="w-100" v-if="content.image" :src="content.image.url"/> 
            <div
              v-for="pin in content.pins" 
              :style="getPinnableAreaLayout(pin)"
              @click="pinClick(pin)" 
              href="javascript:void(0)"
              :class="{'expanded': pin.expanded}"
              class="pinnable-area-outer cursor-pointer" 
              :key="pin.$id"
            >
              <div class="pin-text bg-white" :class="pin.direction || 'right'">
                <div class="pin-title d-flex align-items-center" v-if="pin.title">
                  <div class="my-md-2 py-1">{{ pin.title }}</div>
                </div>
                <div class="my-md-2 py-1" v-if="pin.url">
                  <a
                    class="text-puke-yellow deco-none text-uppercase d-flex align-items-center"
                    :href="pin.url.url"
                    :target="getTarget(pin.url)"
                    @click.stop.prevent="linkClick(pin)"
                  >{{ pin.url.title }} <i class="fal fa-lg fa-angle-right ml-1"></i></a>
                </div>
              </div>
              <div class="pinnable-area-container">
                <div class="pinnable-area-inner d-flex justify-content-center align-items-center">
                  <i class="fal fa-plus fa-2x text-white"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
[interaction-multiple-pin-component] {
  $animation-duration: .25s;
  $dimensions: (
    xs: (
      "pin": 1.75rem,
      "icon": 1.5rem,
      "title-size": 1rem,
      "cta-size": .75rem
    ),
    sm: (
      "pin": 2rem,
      "icon": 1.75rem,
      "title-size": 1rem,
      "cta-size": .75rem
    ),
    md: (
      "pin": 2.5rem,
      "icon": 2rem,
      "title-size": 1.25rem,
      "cta-size": .875rem
    ),
    lg: (
      "pin": 3rem,
      "icon": 2.25rem,
      "title-size": 1.25rem,
      "cta-size": .875rem
    ),
    xl: (
      "pin": 3.5rem,
      "icon": 2.5rem,
      "title-size": 1.25rem,
      "cta-size": .875rem
    )
  );

  .pinnable-area-outer {
    position: absolute;
    border: 1px solid $puke-yellow;

    .pin-text {
      width: 0;
      height: 0;
      overflow: hidden;
      position: absolute;
      transition: all $animation-duration;
    }

    .pinnable-area-container {
      position: relative;

      .pinnable-area-inner {
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        position: absolute;
        background-color: $puke-yellow;
      }

      &, & > i { transition: all $animation-duration; }
    }
  }

  .pinnable-area-outer.expanded {
    border: 0;
    z-index: 1;
    
    .pinnable-area-container {
      margin: 0;
      i { transform: rotate(45deg); }
    }

    .pin-text {
      top: 0;
      width: fit-content;
      height: fit-content;
      white-space: nowrap;
      

      &.left {
        right: 0;
        padding-left: 1rem;
      }
      &.right {
        left: 0;
        padding-right: 1rem;
      }

      .pin-title {
        font-family: $bressay-font;
      }
    }
  }

  @each $size, $value in $dimensions {
    $pin-width: map-get($value, "pin");
    $icon-width: map-get($value, "icon");
    $title-size: map-get($value, "title-size");
    $cta-size: map-get($value, "cta-size");

    @include media-breakpoint-up($size) {
      .pinnable-area-outer {
        .pin-text {
          top: calc(#{$pin-width}*0.16666667);
          &.left { right: calc(#{$pin-width}*0.16666667); }
          &.right { left: calc(#{$pin-width}*0.16666667); }
        }
        .pinnable-area-container {
          width: $pin-width;
          height: $pin-width;
          margin: calc(#{$pin-width}*0.16666667);
          i { font-size: $icon-width; }
        }

        &.expanded {
          .pin-text.right { padding-left: calc(#{$pin-width} + 1rem); }
          .pin-text.left { padding-right: calc(#{$pin-width} + 1rem); }
          .pin-text a { font-size: $cta-size; }
          .pin-title {
            min-height: $pin-width;
            font-size: $title-size !important;
          }
        }
      }
    }
  }
}
</style>